import { useCallback, useRef, type ReactNode } from 'react';

import { useIsMobile } from '../hooks/useMediaQuery';
import { useModalDialog } from '../hooks/useModalDialog';

import { BottomSheet } from './BottomSheet';
import s from './Dialog.module.css';
import { IconButton } from './IconButton';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  labelledById: string;
  describedById?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Set false for a gate the user must complete (Phase 126's complete-profile
   * modal): ESC and backdrop clicks stop dismissing it. A non-dismissible
   * dialog MUST still offer an explicit way out in its own content — a sign-out
   * or cancel control — or it is a trap (jsx-a11y `escape-routes`).
   */
  dismissible?: boolean;
  /**
   * How this dialog presents below the desktop breakpoint. 'card' (default)
   * is the centred panel at every width. 'sheet' rises from the bottom edge on
   * a phone — right for a form or a picker, wrong for a two-button confirm,
   * which is why it is opt-in.
   */
  mobilePresentation?: 'card' | 'sheet';
  /** Sheet only — resting heights as fractions of the viewport, ascending. */
  snapPoints?: readonly number[];
}

// Thin wrapper around the platform <dialog> element. Provides focus trap,
// ESC-to-close, and ::backdrop click-to-close for free — no portal needed
// because <dialog> renders in the browser's top-layer.
export function Dialog({
  mobilePresentation = 'card',
  snapPoints,
  ...props
}: DialogProps): JSX.Element {
  const isMobile = useIsMobile();
  // Two components rather than one early return: each owns its own hooks, so
  // rotating a phone across the breakpoint swaps the element cleanly instead
  // of changing the hook order inside one.
  if (mobilePresentation === 'sheet' && isMobile) {
    const { size: _size, ...sheetProps } = props;
    return <BottomSheet {...sheetProps} {...(snapPoints === undefined ? {} : { snapPoints })} />;
  }
  return <DialogCard {...props} />;
}

function DialogCard({
  open,
  onClose,
  labelledById,
  describedById,
  children,
  size = 'md',
  dismissible = true,
}: Omit<DialogProps, 'mobilePresentation' | 'snapPoints'>): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  useModalDialog(ref, open);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Click outside the inner panel closes the dialog. The <dialog> element's
  // padding region IS the visual gap between viewport and panel, so a click
  // landing on it (target === dialog) is a backdrop click.
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      if (dismissible && event.target === ref.current) {
        onClose();
      }
    },
    [dismissible, onClose],
  );

  // `cancel` is what the native <dialog> fires for ESC. Preventing it is the
  // only way to keep a blocking dialog open — `onClose` fires too late, after
  // the element has already closed itself.
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      if (!dismissible) event.preventDefault();
    },
    [dismissible],
  );

  const sizeClass = size === 'sm' ? s.sm : size === 'lg' ? s.lg : s.md;

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events,
                      jsx-a11y/no-noninteractive-element-interactions,
                      jsx-a11y/no-static-element-interactions --
       Backdrop click on the <dialog> backdrop closes the modal; the native
       <dialog> element already handles ESC dismissal via onClose, so no
       key handler is needed. The inner <div> only stops click propagation
       so clicks inside the panel don't bubble to the backdrop handler. */
    <dialog
      ref={ref}
      aria-labelledby={labelledById}
      aria-describedby={describedById}
      className={`${s.root ?? ''} ${sizeClass ?? ''}`}
      onClose={handleClose}
      onCancel={handleCancel}
      onClick={handleClick}
    >
      <div className={s.panel} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </dialog>
    /* eslint-enable jsx-a11y/click-events-have-key-events,
                     jsx-a11y/no-noninteractive-element-interactions,
                     jsx-a11y/no-static-element-interactions */
  );
}

export function DialogHeader({
  titleId,
  title,
  subtitle,
  onClose,
}: {
  titleId: string;
  title: string;
  subtitle?: string;
  /** Omit on a non-dismissible dialog — no close button is rendered. */
  onClose?: (() => void) | undefined;
}): JSX.Element {
  return (
    <header className={s.head}>
      <div>
        <h2 id={titleId} className={s.title}>
          {title}
        </h2>
        {subtitle !== undefined ? <p className={s.subtitle}>{subtitle}</p> : null}
      </div>
      {onClose !== undefined ? (
        <IconButton label="Close dialog" onClick={onClose} className={s.closeBtn}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 4l10 10M14 4L4 14" />
          </svg>
        </IconButton>
      ) : null}
    </header>
  );
}

export function DialogBody({ children }: { children: ReactNode }): JSX.Element {
  return <div className={s.body}>{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }): JSX.Element {
  return <footer className={s.foot}>{children}</footer>;
}
